import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Check, Loader2, CalendarCheck, ShoppingBag, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useCart } from "@/lib/cart";
import { WHATSAPP_NUMBER } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["10:00 AM", "11:30 AM", "01:00 PM", "02:30 PM", "04:00 PM", "05:30 PM"];

const BookPage = () => {
  const { items, remove, clear } = useCart();
  const navigate = useNavigate();
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const dateLabel = date ? date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) {
      toast.error("Please pick a date and a time slot.");
      return;
    }
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setSending(true);
    const payload = {
      ...form,
      date: dateLabel,
      slot,
      items: items.map((i) => `${i.name} (${i.price})`),
    };
    try {
      await axios.post(`${API}/bookings`, payload);
      setDone({ date: dateLabel, slot, via: "email" });
      clear();
    } catch {
      const lines = [
        "Hi Kiki Services! I would like to book a consultation.",
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        form.phone ? `Phone: ${form.phone}` : null,
        `Preferred: ${dateLabel} at ${slot} IST`,
        payload.items.length ? `Products: ${payload.items.join(", ")}` : null,
        form.message ? `Message: ${form.message}` : null,
      ].filter(Boolean);
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
      setDone({ date: dateLabel, slot, via: "whatsapp" });
      clear();
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-kiki-line focus:border-kiki-gold/60 rounded-xl px-5 py-3.5 text-kiki-ink placeholder:text-kiki-muted/60 outline-none transition-colors duration-300 font-light";

  return (
    <div data-testid="book-page" className="bg-[#FAF5EC] min-h-screen text-kiki-ink warm-spotlight">
      <header className="border-b border-kiki-line bg-[#FAF5EC]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link data-testid="book-back-link" to="/" className="flex items-center gap-2.5 text-sm text-kiki-muted hover:text-kiki-gold transition-colors duration-300 font-outfit">
            <ArrowLeft className="w-4 h-4" />
            <img src="/logo.png" alt="Kiki Services" className="w-8 h-8 rounded-full object-cover border border-kiki-line" />
            <span className="font-display text-xl text-kiki-ink">Kiki<span className="italic text-kiki-gold">Services</span></span>
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {done ? (
          <Reveal>
            <div data-testid="booking-success" className="max-w-xl mx-auto text-center rounded-2xl border border-kiki-line bg-white p-10">
              <span className="mx-auto w-16 h-16 rounded-full bg-kiki-surface flex items-center justify-center text-kiki-gold">
                <CalendarCheck className="w-7 h-7" />
              </span>
              <h1 className="font-display text-4xl font-medium text-kiki-ink mt-6">Request received</h1>
              <p className="mt-3 text-kiki-muted font-light">
                Your consultation request for <strong className="text-kiki-ink">{done.date}</strong> at{" "}
                <strong className="text-kiki-ink">{done.slot} IST</strong> is in.{" "}
                {done.via === "whatsapp"
                  ? "WhatsApp has opened with your booking details — just press send, and we will confirm shortly."
                  : "A confirmation email is on its way — we will share the meeting link shortly."}
              </p>
              <button
                data-testid="booking-home-btn"
                onClick={() => navigate("/")}
                className="mt-8 inline-flex items-center gap-2 bg-kiki-ink hover:bg-kiki-gold text-[#FAF5EC] font-outfit font-semibold rounded-full px-8 py-3.5 transition-colors duration-300"
              >
                Back to home
              </button>
            </div>
          </Reveal>
        ) : (
          <>
            <Reveal><Eyebrow>Book a consultation</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h1 className="font-display text-4xl sm:text-6xl font-medium tracking-tight text-kiki-ink mt-3 leading-[1.05]">
                Pick a time,<br /><span className="italic text-kiki-gold">we will bring the plan</span>
              </h1>
            </Reveal>

            <form data-testid="booking-form" onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
              <Reveal delay={0.15}>
                <div className="rounded-2xl border border-kiki-line bg-white p-6 sm:p-8">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-kiki-muted mb-4">1 · Choose a date</p>
                  <div data-testid="booking-calendar" className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0}
                    />
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-kiki-muted mt-6 mb-3">2 · Choose a slot (IST)</p>
                  <p className="text-xs text-kiki-muted font-light mb-3">Monday–Saturday, 10 AM – 7 PM IST · 45-minute sessions with a 45-minute gap between bookings</p>
                  <div className="flex flex-wrap gap-2.5">
                    {SLOTS.map((s, i) => (
                      <button
                        type="button"
                        key={s}
                        data-testid={`slot-btn-${i}`}
                        onClick={() => setSlot(s)}
                        className={`rounded-full border px-4 py-2 text-sm font-outfit transition-colors duration-300 ${
                          slot === s
                            ? "bg-kiki-gold border-kiki-gold text-[#FAF5EC]"
                            : "border-kiki-line text-kiki-ink hover:border-kiki-gold/50 hover:text-kiki-gold"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  {date && slot && (
                    <p data-testid="booking-selection" className="mt-5 text-sm text-kiki-gold font-outfit font-semibold">
                      {dateLabel} · {slot} IST
                    </p>
                  )}
                </div>
              </Reveal>

              <Reveal delay={0.25}>
                <div className="rounded-2xl border border-kiki-line bg-white p-6 sm:p-8 space-y-5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-kiki-muted">3 · Your details</p>

                  {items.length > 0 && (
                    <div data-testid="booking-cart-items" className="rounded-xl bg-kiki-surface/70 border border-kiki-line p-4">
                      <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-kiki-muted mb-2.5">
                        <ShoppingBag className="w-3.5 h-3.5 text-kiki-gold" /> Shortlisted products
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {items.map((item, i) => (
                          <span key={item.name} className="flex items-center gap-1.5 text-xs font-outfit bg-white border border-kiki-line rounded-full px-3 py-1.5 text-kiki-ink">
                            {item.name}
                            <button type="button" data-testid={`booking-item-remove-${i}`} onClick={() => remove(item.name)} aria-label={`Remove ${item.name}`} className="text-kiki-muted hover:text-kiki-gold">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input data-testid="booking-name-input" value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} />
                    <input data-testid="booking-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email address *" className={inputCls} />
                  </div>
                  <input data-testid="booking-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" className={inputCls} />
                  <textarea
                    data-testid="booking-message-input"
                    value={form.message}
                    onChange={set("message")}
                    placeholder="What would you like to discuss?"
                    rows={4}
                    className={`${inputCls} resize-none`}
                  />
                  <button
                    data-testid="booking-submit-btn"
                    type="submit"
                    disabled={sending}
                    className="flex items-center justify-center gap-2 w-full bg-kiki-ink hover:bg-kiki-gold disabled:opacity-60 text-[#FAF5EC] font-outfit font-semibold rounded-full px-7 py-4 transition-colors duration-300"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {sending ? "Booking…" : "Request booking"}
                  </button>
                  <p className="text-[11px] text-kiki-muted font-light text-center">
                    No payment now — this books a free introductory call.
                  </p>
                </div>
              </Reveal>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default BookPage;
